import { Procedure } from '../define-procedure';
import { Store } from '@ngxs/store';
import { RPCEvent } from '../define-event';
import { MessageEnum } from '../models/message.enum';
import { LabelsService } from 'src/app/story/labels.service';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { AnnotationModel } from 'src/app/story/models/labels/annotation.model';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { AddLabel, RemoveLabel, SetLabel, SetLabelsShown, SetAllLabelsShown } from 'src/app/story/state/labels/story.labels.actions';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { MarkerModel } from 'src/app/story/models/marker.model';
import { HumanInterface } from '../models/human.interface';
import { AnnotationsIO } from '../models/annotations.io';

function getDistinct<T>(values: T[]): T[] {
  const valuesSet = new Set(values);
  return Array.from(valuesSet.values());
}

function getLabels(store: Store): LabelModel[] {
  return store.selectSnapshot(StoryLabelsState.getChapterLabels);
}

function getLabelById(store: Store, labelId: string): LabelModel {
  const labels = getLabels(store);
  return findLabelById(labels, labelId);
}

function getMarkerById(human: HumanInterface, labelId: string): MarkerModel {
  return human.scene.markers.markers[labelId];
}

function findLabelById(labels: LabelModel[], labelId: string): LabelModel {
  return labels.find((label) => labelId === label.id);
}

function findLabelsByIds(labels: LabelModel[], labelIds: string[]): LabelModel[] {
  return labels.filter((label) => labelIds.includes(label.id));
}

function findLabelsByObjectId(labels: LabelModel[], ...objectIds: string[]): LabelModel[] {
  const allFoundLabels = objectIds.map((objectId) => {
    const labelsByObjectId = labels.filter((label) => objectId === label.objectId);
    return labelsByObjectId;
  });
  // flatten list
  const flatLabels = [].concat(...allFoundLabels);
  return getDistinct(flatLabels);
}

function convertAnnotationToLabel(
  human: HumanInterface,
  store: Store,
  labelsService: LabelsService,
  request: AnnotationsIO.AnnotationsCreateProcRequest
): LabelModel {
  const chapter = store.selectSnapshot(StoryChaptersState.getSelectedChapter);

  // if the request objectId isn't in the scene, replace it with undefined.
  const objectIdSanitized = human.scene.objects[request.objectId]
    ? request.objectId
    : undefined;

  // API annotation request -> app annotation
  const annotation: AnnotationModel = {
    annotationId: request.annotationId,
    title: request.title,
    description: request.description,
    objectId: objectIdSanitized,
    pos: request.position,
    occludable: request.occludable,
    labelOffset: request.labelOffset,
    shown: request.shown,
    labelShown: request.shown
  };

  // Prevent labels from being unoccludable.
  // The engine requires markers to have a defined position if they are to be occludable.
  // If the request has an object Id, and no position, assign a position.
  // https://github.com/biodigital-human/human-engine/blob/master/src/scene/markers.js#L121
  // https://github.com/biodigital-human/human-engine/blob/master/src/scene/markers.js#L121
  if (annotation.objectId && annotation.pos === undefined) {
    annotation.pos = human.scene.objects[annotation.objectId].center;
  }

  // app annotation -> app label
  return labelsService.deserializeAnnotation(
    annotation,
    chapter,
    LabelThemeEnum.Annotation
  );
}


function convertLabelToAnnotation(human: HumanInterface, label: LabelModel): AnnotationsIO.AnnotationResponse {
  // associated marker
  const marker: MarkerModel = getMarkerById(human, label.id);

  // app label -> API annotation response
  return {
    annotationId: label.id,
    objectId: label.objectId || null,
    title: label.title,
    description: label.description,
    shown: label.shown,
    occludable: label.occludable,
    // live position tracked by marker
    position: marker
      ? {
          x: marker.pos[0],
          y: marker.pos[1],
          z: marker.pos[2]
        }
      : null,
    canvasPosition: marker
      ? {
          x: marker.canvasPos[0],
          y: marker.canvasPos[1]
        }
      : null
  };
  
}

function convertLabelsToAnnotationsInfo(human: HumanInterface, labels: LabelModel[]): AnnotationsIO.AnnotationsInfo {
  // annotation id -> annotation info
  return labels.reduce((info, label) => {
    info[label.id] = convertLabelToAnnotation(human, label);
    return info;
  }, {});
}

function createLabelFromAnnotation(store: Store, annotation: AnnotationsIO.AnnotationsUpdateProcRequest): LabelModel {
  const label = getLabelById(store, annotation.annotationId);
  if (label) {
    return {
      ...label,
      ...annotation
    };
  } else {
    return null;
  }
}

function createLabel(store: Store, label: LabelModel): LabelModel {
  // validate required fields for label creation
  if (label.objectId || label.pos) {
    store.dispatch(new AddLabel(label));
    return label;
  } else {
    return null;
  }
}

function updateLabel(store: Store, label: LabelModel): boolean {
  // patch label w/ annotation data
  if (label) {
    store.dispatch(new SetLabel(label));
  }
  return true;
}

function deleteLabel(store: Store, label: LabelModel): boolean {
  if (label) {
    store.dispatch(new RemoveLabel(label));
  }
  return true;
}

function showAllLabels(store: Store, shown: boolean) {
  store.dispatch(new SetAllLabelsShown(shown));
  return true;
}

function createIdMapFromValue<T>(ids: string[], value: T): AnnotationsIO.IdMap<T> {
  return ids.reduce((acc, id) => ({ ...acc, [`${id}`]: value }), {});
}

function filterMap<T>(idMap: AnnotationsIO.IdMap<T>, compareFn: (key: string, value: T) => boolean): AnnotationsIO.IdMap<T> {
  return Object.entries(idMap)
    .filter(([key, value]) => compareFn(key, value))
    .reduce((acc, [key, value]) => ({ ...acc, [`${key}`]: value }), {});
}

function filterMapByValue<T>(idMap: AnnotationsIO.IdMap<T>, compareValue: T): AnnotationsIO.IdMap<T> {
  return filterMap(idMap, (key, value) => value === compareValue);
}

function filterIdMapKeysByValue<T>(idMap: AnnotationsIO.IdMap<T>, compareValue: T): string[] {
  return Object.keys(
    filterMapByValue(idMap, compareValue)
  );
}

function filterLabelsByMapValue<T>(labels: LabelModel[], idMap: AnnotationsIO.IdMap<T>, compareValue: T): LabelModel[] {
  const labelIds = filterIdMapKeysByValue(idMap, compareValue);
  return findLabelsByIds(labels, labelIds);
}

function createLabelMapFromObjectIdMap<T>(labels: LabelModel[], objectIdMap: AnnotationsIO.IdMap<T>, compareValue: T): AnnotationsIO.IdMap<T> {
  const allIds = filterIdMapKeysByValue(objectIdMap, compareValue) // get list of object ids
    .map((objectId) => findLabelsByObjectId(labels, objectId)) // get labels by object
    .map((objectLabels) => objectLabels.map((a) => a.id)); // get ids
  const flatIds = [].concat(...allIds);
  return createIdMapFromValue(flatIds, compareValue);
}

function getLabelsShownMap(store: Store, params: AnnotationsIO.SetLabelsShownRequest): AnnotationsIO.IdMap<boolean> {
  const { annotations, objects, replace, type } = params;
  const annotationsIdMap = annotations || {};
  const objectsIdsMap = objects || {};
  const labels = getLabels(store);

  const idShownMap: AnnotationsIO.IdMap<boolean> = {
    // filter by annotation id
    ...filterMapByValue(annotationsIdMap, true),
    ...filterMapByValue(annotationsIdMap, false),
    // Show/hide labels for annotations on target objects
    ...createLabelMapFromObjectIdMap(labels, objectsIdsMap, true),
    ...createLabelMapFromObjectIdMap(labels, objectsIdsMap, false)
    // #TODO[AC]: filter by type???
  };

  // replace: hide all others
  if (replace) {
    return {
      // hide all labels
      ...createIdMapFromValue(labels.map((label) => label.id), false),
      // replace w/ target config
      ...idShownMap
    };
  } else {
    return idShownMap;
  }
}

function setLabelsShown(store: Store, labelShownMap: AnnotationsIO.IdMap<boolean>) {
  const labels = getLabels(store);
  const labelsToHide = filterLabelsByMapValue(labels, labelShownMap, false);
  const labelsToShow = filterLabelsByMapValue(labels, labelShownMap, true);

  // show labels
  store.dispatch(new SetLabelsShown(labelsToShow, true));
  // hide labels
  store.dispatch(new SetLabelsShown(labelsToHide, false));
}

export function getAnnotationEvents(human: HumanInterface, store: Store): RPCEvent<AnnotationsIO.AnnotationEventResponse>[] {
  return [
    {
      eventName: EventsEnum.AnnotationsCreated,
      aliasEvents: [EventsEnum.LabelsCreated],
      mapFn: (label: LabelModel) => convertLabelToAnnotation(human, label)
    },
    {
      eventName: EventsEnum.AnnotationsDestroyed,
      aliasEvents: [EventsEnum.LabelsDestroyed],
      mapFn: (label: LabelModel) => ({ annotationId: label.id })
    },
    {
      eventName: EventsEnum.AnnotationsShown,
      aliasEvents: [EventsEnum.LabelsEnabled],
      mapFn: () => true
    },
    {
      eventName: EventsEnum.AnnotationsHidden,
      aliasEvents: [EventsEnum.LabelsDisabled],
      mapFn: () => true
    }
  ];
}

export function getAnnotationProcedures(
  human: HumanInterface,
  store: Store,
  labelsService: LabelsService
): Procedure<AnnotationsIO.AnnotationProcRequest, AnnotationsIO.AnnotationProcResponse>[] {
  return [
    {
      procName: MessageEnum.AnnotationsInfo,
      actionFn: () => getLabels(store),
      mapFnOut: (labels: LabelModel[]) => convertLabelsToAnnotationsInfo(human, labels)
    },
    {
      procName: MessageEnum.AnnotationsCreate,
      mapFnIn: (request: AnnotationsIO.AnnotationsCreateProcRequest) => convertAnnotationToLabel(human, store, labelsService, request),
      actionFn: (label: LabelModel) => createLabel(store, label),
      mapFnOut: (label: LabelModel) => label ? convertLabelToAnnotation(human, label) : null
    },
    {
      procName: MessageEnum.AnnotationsUpdate,
      mapFnIn: (params: AnnotationsIO.AnnotationsUpdateProcRequest) => createLabelFromAnnotation(store, params),
      actionFn: (label: LabelModel) => updateLabel(store, label)
    },
    {
      procName: MessageEnum.AnnotationsSetTitle,
      mapFnIn: (params: AnnotationsIO.AnnotationsUpdateProcRequest) => createLabelFromAnnotation(store, params),
      actionFn: (label: LabelModel) => updateLabel(store, label),
      hideDefinition: true
    },
    {
      procName: MessageEnum.AnnotationsSetDescription,
      mapFnIn: (params: AnnotationsIO.AnnotationsUpdateProcRequest) => createLabelFromAnnotation(store, params),
      actionFn: (label: LabelModel) => updateLabel(store, label),
      hideDefinition: true
    },
    {
      procName: MessageEnum.AnnotationsDestroy,
      mapFnIn: (annotationId: AnnotationsIO.AnnotationsDestroyRequest) => getLabelById(store, annotationId),
      actionFn: (label: LabelModel) => deleteLabel(store, label)
    },
    {
      procName: MessageEnum.ShowAnnotations,
      actionFn: () => showAllLabels(store, true)
    },
    {
      procName: MessageEnum.SetAnnotationsShown,
      actionFn: () => showAllLabels(store, true),
      hideDefinition: true
    },
    {
      procName: MessageEnum.HideAnnotations,
      actionFn: () => showAllLabels(store, false)
    },
    {
      procName: MessageEnum.AnnotationsSetLabelsShown,
      mapFnIn: (params: AnnotationsIO.SetLabelsShownRequest) => getLabelsShownMap(store, params),
      actionFn: (labelShownMap: AnnotationsIO.IdMap<boolean>) => setLabelsShown(store, labelShownMap),
      hideDefinition: true
    }
  ];
}
